import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Check, ChevronLeft, ChevronDown, ChevronUp, Star, Mail, User, Phone, Info, Zap, CreditCard, Copy, Building2, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { WireframeGlobe } from '../components/WireframeGlobe';
// EmailJS configuration — sends reservation details to your email when the user confirms
const EMAILJS_SERVICE_ID = 'service_qvmqlhi';
const EMAILJS_TEMPLATE_ID = 'template_7ynesvi';
const EMAILJS_PUBLIC_KEY = '-083vwxCoxw7cbSCz';
// Bank transfer details
const IBAN = 'ES65 2100 0071 1702 0099 5514';
const BENEFICIARY = 'Akram Azghoul Atnibar';
export function CheckoutPage() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [proofSent, setProofSent] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handlePay = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    // Send reservation details via EmailJS, then show bank transfer instructions
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || 'Sin notas adicionales'
      }, EMAILJS_PUBLIC_KEY);
    } catch (error) {
      console.error('EmailJS error:', error);
      // Continue to instructions even if email fails — user shouldn't lose their reservation flow
    }
    setIsSubmitting(false);
    setIsConfirmed(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(IBAN.replace(/\s/g, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Clipboard error:', e);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const includedItems = ['Inicio inmediato del proyecto', 'Consulta estratégica personalizada', 'Diseño y desarrollo profesional', 'Soporte prioritario continuo'];
  return <div className="min-h-screen bg-[#0a0a0a] relative selection:bg-brand-emerald/20 selection:text-white font-sans antialiased" data-id="element-188">
      <div className="flex flex-col lg:flex-row min-h-screen" data-id="element-189">
        {/* ==================== LEFT PANEL ==================== */}
        <div className="lg:w-[45%] xl:w-[42%] relative bg-[#0a0a0a] lg:border-r border-white/[0.05]" data-id="element-190">
          <div className="max-w-[440px] ml-auto px-6 md:px-10 lg:pr-16 pt-8 lg:pt-12 pb-8 lg:pb-16 relative z-10" data-id="element-191">
            <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-192">
              {/* Back — Desktop */}
              <motion.button variants={itemVariants} onClick={() => navigate(-1)} className="hidden lg:flex items-center gap-2 text-gray-500 hover:text-gray-300 text-[11px] uppercase tracking-[0.2em] font-medium mb-12 transition-colors" data-id="element-193">
                <ChevronLeft size={14} data-id="element-194" />
                Volver
              </motion.button>

              {/* Mobile Header */}
              <div className="flex lg:hidden items-center justify-between mb-8" data-id="element-195">
                <button onClick={() => navigate(-1)} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-white transition-colors" data-id="element-196">
                  <ChevronLeft size={18} data-id="element-197" />
                </button>
                <div className="flex items-center gap-3" data-id="element-198">
                  <div className="w-8 h-8 rounded-lg bg-[#111] border border-white/[0.08] flex items-center justify-center overflow-hidden" data-id="element-199">
                    <WireframeGlobe size={44} data-id="element-200" />
                  </div>
                  <span className="text-white font-medium text-sm tracking-wide" data-id="element-201">
                    Growthly
                  </span>
                </div>
                <div className="w-10" data-id="element-202" />
              </div>

              {/* Brand — Desktop */}
              <motion.div variants={itemVariants} className="hidden lg:flex items-center gap-4 mb-12" data-id="element-203">
                <div className="w-12 h-12 rounded-xl bg-[#111] border border-white/[0.1] flex items-center justify-center overflow-hidden" data-id="element-204">
                  <WireframeGlobe size={64} data-id="element-205" />
                </div>
                <div data-id="element-206">
                  <span className="text-white font-medium text-lg tracking-tight block mb-0.5" data-id="element-207">
                    Growthly
                  </span>
                  <span className="text-gray-500 text-[10px] uppercase tracking-[0.2em] font-medium" data-id="element-208">
                    Digital Ecosystem Agency
                  </span>
                </div>
              </motion.div>

              {/* Price */}
              <motion.div variants={itemVariants} className="mb-8" data-id="element-209">
                <p className="text-gray-500 text-[11px] font-medium uppercase tracking-[0.2em] mb-3" data-id="element-210">
                  Reserva de Proyecto
                </p>
                <div className="flex items-baseline gap-1.5" data-id="element-211">
                  <span className="text-5xl font-medium text-white leading-none tracking-tight tabular-nums" data-id="element-212">
                    50
                  </span>
                  <span className="text-5xl font-medium text-white/30 leading-none tracking-tight tabular-nums" data-id="element-213">
                    ,00
                  </span>
                  <span className="text-gray-500 text-sm font-medium ml-1 self-end mb-1" data-id="element-214">
                    EUR
                  </span>
                </div>
              </motion.div>

              {/* Mobile Expand */}
              <div className="lg:hidden mb-8" data-id="element-215">
                <button onClick={() => setIsMobileSummaryExpanded(!isMobileSummaryExpanded)} className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-white/[0.02] border border-white/[0.05] text-sm text-gray-400 font-medium transition-colors" data-id="element-216">
                  <span data-id="element-217">
                    {isMobileSummaryExpanded ? 'Ocultar detalles' : 'Ver detalles del pedido'}
                  </span>
                  {isMobileSummaryExpanded ? <ChevronUp size={16} className="text-gray-500" data-id="element-218" /> : <ChevronDown size={16} className="text-gray-500" data-id="element-219" />}
                </button>
              </div>

              {/* Expandable Details */}
              <div className={`${!isMobileSummaryExpanded ? 'hidden lg:block' : ''}`} data-id="element-220">
                {/* Deposit Explanation */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-221">
                  <div className="bg-[#111] border border-white/[0.05] rounded-2xl p-5" data-id="element-222">
                    <div className="flex items-center gap-2 mb-4" data-id="element-223">
                      <div className="w-6 h-6 rounded-full bg-white/[0.05] flex items-center justify-center" data-id="element-224">
                        <Info size={14} className="text-gray-400" data-id="element-225" />
                      </div>
                      <h3 className="text-white font-medium text-[14px]" data-id="element-226">
                        ¿Por qué una reserva de 50€?
                      </h3>
                    </div>

                    <div className="space-y-4" data-id="element-227">
                      <div className="flex gap-3" data-id="element-228">
                        <CreditCard size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-229" />
                        <div data-id="element-230">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-231">
                            Se descuenta del precio final
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-232">
                            Los 50€ se restan íntegramente del presupuesto
                            total.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-233">
                        <Zap size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-234" />
                        <div data-id="element-235">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-236">
                            Inicio inmediato
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-237">
                            Activa el inicio del proyecto de forma prioritaria.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-238">
                        <ShieldCheck size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-239" />
                        <div data-id="element-240">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-241">
                            Sin riesgo
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-242">
                            Garantía de devolución del 100% si no estás
                            satisfecho.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-243" />

                {/* Product card */}
                <motion.div variants={itemVariants} className="mb-8" data-id="element-244">
                  <div className="rounded-xl bg-[#111] border border-white/[0.05] overflow-hidden" data-id="element-245">
                    <div className="p-5 flex items-center gap-4" data-id="element-246">
                      <div className="w-12 h-12 rounded-xl bg-[#0a0a0a] border border-white/[0.05] flex items-center justify-center shrink-0" data-id="element-247">
                        <WireframeGlobe size={32} data-id="element-248" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-249">
                        <h3 className="text-white font-medium text-[15px] mb-0.5" data-id="element-250">
                          Ecosistema Digital
                        </h3>
                        <p className="text-gray-500 text-[12px]" data-id="element-251">
                          Abono inicial · Proyecto completo
                        </p>
                      </div>
                      <div className="text-white font-medium text-[15px] tabular-nums" data-id="element-252">
                        50,00€
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Included */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-253">
                  <p className="text-[11px] text-gray-500 font-medium mb-4" data-id="element-254">
                    Incluido en tu reserva:
                  </p>
                  <div className="space-y-3" data-id="element-255">
                    {includedItems.map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-256">
                        <Check size={14} className="text-brand-emerald shrink-0" data-id="element-257" />
                        <span className="text-gray-400 text-[13px]" data-id="element-258">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-259" />

                {/* Totals */}
                <motion.div variants={itemVariants} className="space-y-3 mb-10" data-id="element-260">
                  <div className="flex justify-between text-[13px]" data-id="element-261">
                    <span className="text-gray-500" data-id="element-262">Subtotal</span>
                    <span className="text-gray-300 tabular-nums" data-id="element-263">50,00€</span>
                  </div>
                  <div className="flex justify-between text-[13px]" data-id="element-264">
                    <span className="text-gray-500" data-id="element-265">IVA</span>
                    <span className="text-gray-300" data-id="element-266">Incluido</span>
                  </div>
                  <div className="pt-3 mt-3 border-t border-white/[0.06] flex justify-between items-center" data-id="element-267">
                    <span className="text-white font-medium text-[15px]" data-id="element-268">
                      Total a pagar hoy
                    </span>
                    <div className="flex items-baseline gap-1" data-id="element-269">
                      <span className="text-white font-medium text-xl tabular-nums" data-id="element-270">
                        50,00
                      </span>
                      <span className="text-gray-400 text-sm" data-id="element-271">€</span>
                    </div>
                  </div>
                </motion.div>

                {/* Social proof */}
                <motion.div variants={itemVariants} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[#111] border border-white/[0.05]" data-id="element-272">
                  <div className="flex gap-0.5" data-id="element-273">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-brand-emerald fill-brand-emerald" data-id="element-274" />)}
                  </div>
                  <div className="text-gray-400 text-[12px]" data-id="element-275">
                    <span className="text-white font-medium" data-id="element-276">+4.000</span>{' '}
                    proyectos completados
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ==================== RIGHT PANEL ==================== */}
        <div className="w-full lg:w-[55%] xl:w-[58%] bg-[#0a0a0a] min-h-screen relative" data-id="element-277">
          <div className="max-w-[560px] mx-auto lg:mx-0 lg:mr-auto px-6 md:px-10 lg:pl-16 pt-12 lg:pt-20 pb-36 lg:pb-24 relative z-10" data-id="element-278">
            {!isConfirmed ? <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-279">
                {/* Form Container */}
                <div className="bg-[#111] border border-white/[0.05] rounded-3xl p-6 md:p-10" data-id="element-280">
                  {/* Section 01 */}
                  <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-281">
                    <span className="text-[28px] font-light text-gray-600 leading-none" data-id="element-282">
                      01
                    </span>
                    <div data-id="element-283">
                      <h2 className="text-white font-medium text-lg" data-id="element-284">
                        Contacto
                      </h2>
                      <p className="text-gray-500 text-[13px] mt-1" data-id="element-285">
                        Para enviarte la confirmación del proyecto
                      </p>
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div variants={itemVariants} className="mb-8" data-id="element-286">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-287">
                      Correo electrónico
                    </label>
                    <div className="relative" data-id="element-288">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-289">
                        <Mail size={16} className={`transition-colors duration-300 ${focusedField === 'email' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-290" />
                      </div>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.email ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 transition-all duration-300 placeholder:text-gray-700`} placeholder="tu@correo.com" data-id="element-291" />
                    </div>
                    {errors.email && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-292">
                        {errors.email}
                      </p>}
                  </motion.div>

                  <motion.div variants={itemVariants} className="h-px bg-white/[0.06] my-8" data-id="element-293" />

                  {/* Section 02 */}
                  <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-294">
                    <span className="text-[28px] font-light text-gray-600 leading-none" data-id="element-295">
                      02
                    </span>
                    <div data-id="element-296">
                      <h2 className="text-white font-medium text-lg" data-id="element-297">
                        Datos personales
                      </h2>
                    </div>
                  </motion.div>

                  {/* Name + Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-298">
                    <motion.div variants={itemVariants} data-id="element-299">
                      <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-300">
                        Nombre completo
                      </label>
                      <div className="relative" data-id="element-301">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-302">
                          <User size={16} className={`transition-colors duration-300 ${focusedField === 'name' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-303" />
                        </div>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.name ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 transition-all duration-300 placeholder:text-gray-700`} placeholder="Juan Pérez" data-id="element-304" />
                      </div>
                      {errors.name && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-305">
                          {errors.name}
                        </p>}
                    </motion.div>

                    <motion.div variants={itemVariants} data-id="element-306">
                      <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-307">
                        Teléfono
                      </label>
                      <div className="relative" data-id="element-308">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-309">
                          <Phone size={16} className={`transition-colors duration-300 ${focusedField === 'phone' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-310" />
                        </div>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.phone ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 transition-all duration-300 placeholder:text-gray-700`} placeholder="+34 600 000 000" data-id="element-311" />
                      </div>
                      {errors.phone && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-312">
                          {errors.phone}
                        </p>}
                    </motion.div>
                  </div>

                  {/* Message */}
                  <motion.div variants={itemVariants} className="mb-8" data-id="element-313">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-314">
                      Notas{' '}
                      <span className="text-gray-600 normal-case tracking-normal" data-id="element-315">
                        (opcional)
                      </span>
                    </label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} rows={3} className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 transition-all duration-300 resize-none placeholder:text-gray-700" placeholder="Detalles sobre tu proyecto..." data-id="element-316" />
                  </motion.div>

                  {/* Confirm Button */}
                  <motion.div variants={itemVariants} data-id="element-317">
                    <button onClick={handlePay} disabled={isSubmitting} className={`w-full flex justify-center items-center gap-2.5 px-6 py-[18px] rounded-xl font-medium text-[15px] transition-all duration-300 ${isSubmitting ? 'bg-brand-emerald/30 text-white/50 cursor-wait' : 'bg-brand-emerald text-[#0a0a0a] hover:bg-brand-emeraldLight'}`} data-id="element-318">
                      {isSubmitting ? <div className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full animate-spin" data-id="element-319" /> : <>
                          <Lock size={16} data-id="element-320" />
                          <span data-id="element-321">Confirmar reserva · 50,00€</span>
                        </>}
                    </button>
                  </motion.div>

                  {/* Transfer note */}
                  <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mt-5" data-id="element-322">
                    <Building2 size={12} className="text-gray-600" data-id="element-323" />
                    <p className="text-gray-500 text-[12px]" data-id="element-324">
                      Pago por transferencia bancaria · Datos al confirmar
                    </p>
                  </motion.div>
                </div>

                {/* Trust row */}
                <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-6 text-[12px] text-gray-500" data-id="element-325">
                  <div className="flex items-center gap-1.5" data-id="element-326">
                    <ShieldCheck size={14} className="text-gray-400" data-id="element-327" />
                    <span data-id="element-328">Datos protegidos</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-329">
                    <Check size={14} className="text-gray-400" data-id="element-330" />
                    <span data-id="element-331">Confirmación inmediata</span>
                  </div>
                </motion.div>

                {/* Direct contact - builds trust before payment */}
                <motion.div variants={itemVariants} className="mt-6 p-5 rounded-2xl bg-[#111] border border-white/[0.05]" data-id="element-332">
                  <p className="text-center text-gray-400 text-[13px] mb-4" data-id="element-333">
                    ¿Tienes dudas antes de reservar? Estamos disponibles.
                  </p>
                  <div className="grid grid-cols-2 gap-3" data-id="element-334">
                    <a href="tel:+34631012592" className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.06] transition-all duration-300 group" data-id="element-335">
                      <Phone size={14} className="text-brand-emerald" data-id="element-336" />
                      <span className="text-white text-[13px] font-medium transition-colors" data-id="element-337">
                        Llamar
                      </span>
                    </a>
                    <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.06] transition-all duration-300 group" data-id="element-338">
                      <svg className="w-3.5 h-3.5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" data-id="element-339">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-340" />
                      </svg>
                      <span className="text-white text-[13px] font-medium transition-colors" data-id="element-341">
                        WhatsApp
                      </span>
                    </a>
                  </div>
                  <p className="text-center text-gray-600 text-[11px] mt-3 font-medium" data-id="element-342">
                    +34 631 01 25 92
                  </p>
                </motion.div>
              </motion.div> /* ==================== CONFIRMATION / TRANSFER INSTRUCTIONS ==================== */ : <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-343">
                {/* Success header */}
                <div className="flex items-center gap-3 mb-8" data-id="element-344">
                  <div className="w-11 h-11 rounded-full bg-brand-emerald/10 flex items-center justify-center shrink-0" data-id="element-345">
                    <CheckCircle2 size={22} className="text-brand-emerald" data-id="element-346" />
                  </div>
                  <div data-id="element-347">
                    <h2 className="text-white font-medium text-xl tracking-tight" data-id="element-348">
                      Reserva registrada
                    </h2>
                    <p className="text-gray-500 text-[13px] mt-0.5" data-id="element-349">
                      Completa la transferencia para activar tu proyecto.
                    </p>
                  </div>
                </div>

                {/* Transfer details card */}
                <div className="bg-[#111] border border-white/[0.08] rounded-2xl p-6 md:p-8 mb-6 shadow-sm" data-id="element-350">
                  <p className="text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-6" data-id="element-351">
                    Datos para la transferencia
                  </p>

                  <div className="space-y-0" data-id="element-352">
                    {/* IBAN */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-3" data-id="element-353">
                      <span className="text-gray-500 text-[13px]" data-id="element-354">IBAN</span>
                      <div className="flex items-center gap-3" data-id="element-355">
                        <code className="text-white text-[14px] tracking-wide tabular-nums font-medium" data-id="element-356">
                          {IBAN}
                        </code>
                        <button onClick={copyIban} className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.05] text-gray-400 hover:text-white transition-colors" title="Copiar IBAN" data-id="element-357">
                          {copied ? <Check size={14} className="text-brand-emerald" data-id="element-358" /> : <Copy size={14} data-id="element-359" />}
                        </button>
                      </div>
                    </div>

                    {/* Bank */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-360">
                      <span className="text-gray-500 text-[13px]" data-id="element-361">Banco</span>
                      <span className="text-white text-[14px] font-medium" data-id="element-362">
                        Caixabank
                      </span>
                    </div>

                    {/* Beneficiary */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-363">
                      <span className="text-gray-500 text-[13px]" data-id="element-364">
                        Beneficiario
                      </span>
                      <span className="text-white text-[14px] font-medium" data-id="element-365">
                        {BENEFICIARY}
                      </span>
                    </div>

                    {/* Concept — fixed transfer reference */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-366">
                      <span className="text-gray-500 text-[13px]" data-id="element-367">
                        Concepto
                      </span>
                      <span className="text-white text-[14px] font-medium" data-id="element-368">
                        Reserva Growthly
                      </span>
                    </div>

                    {/* Representative full name + contact number */}
                    <div className="flex flex-col md:flex-row md:items-start justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-369">
                      <span className="text-gray-500 text-[13px] md:pt-0.5" data-id="element-370">
                        Representante
                      </span>
                      <div className="flex flex-col md:items-end gap-1" data-id="element-371">
                        <span className="text-white text-[14px] font-medium" data-id="element-372">
                          {formData.name.trim() || 'Indicado en tu reserva'}
                        </span>
                        {formData.phone.trim() && <span className="text-gray-400 text-[13px] tabular-nums" data-id="element-373">
                            {formData.phone.trim()}
                          </span>}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 gap-2" data-id="element-374">
                      <span className="text-gray-500 text-[13px]" data-id="element-375">Importe</span>
                      <span className="text-white text-[16px] font-medium tabular-nums" data-id="element-376">
                        50,00€
                      </span>
                    </div>
                  </div>
                </div>

                {/* Send proof */}
                <div className="bg-[#111] border border-white/[0.08] rounded-2xl p-6 mb-6" data-id="element-377">
                  {!proofSent ? <>
                      <p className="text-gray-400 text-[13px] leading-relaxed mb-5" data-id="element-378">
                        Una vez realizada la transferencia, envía el comprobante
                        de pago por WhatsApp a{' '}
                        <span className="text-gray-300 font-medium" data-id="element-379">
                          +34 631 01 25 92
                        </span>{' '}
                        para activar tu proyecto de inmediato.
                      </p>
                      <div className="flex flex-col gap-3" data-id="element-380">
                        <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2.5 w-full py-3.5 px-4 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-colors" data-id="element-381">
                          <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" data-id="element-382">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-383" />
                          </svg>
                          <span className="text-white text-[13px] font-medium" data-id="element-384">
                            Enviar comprobante por WhatsApp
                          </span>
                        </a>
                        <button onClick={() => setProofSent(true)} className="w-full py-3.5 px-4 rounded-xl bg-transparent border border-white/[0.05] text-gray-400 hover:text-white hover:bg-white/[0.02] text-[13px] font-medium transition-colors" data-id="element-385">
                          Ya lo he enviado
                        </button>
                      </div>
                    </> : <div className="flex flex-col items-center justify-center text-center py-4" data-id="element-386">
                      <div className="w-10 h-10 rounded-full bg-brand-emerald/10 flex items-center justify-center mb-4" data-id="element-387">
                        <CheckCircle2 size={20} className="text-brand-emerald" data-id="element-388" />
                      </div>
                      <h3 className="text-white font-medium text-[15px] mb-1.5" data-id="element-389">
                        ¡Gracias por confirmar!
                      </h3>
                      <p className="text-gray-500 text-[13px] leading-relaxed max-w-[280px]" data-id="element-390">
                        Revisaremos el comprobante y activaremos tu proyecto en
                        breve.
                      </p>
                    </div>}
                </div>

                <button onClick={() => navigate('/')} className="w-full flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.05] text-gray-300 hover:bg-white/[0.06] text-[14px] font-medium transition-colors" data-id="element-391">
                  Volver al inicio
                </button>
              </motion.div>}
          </div>

          {/* Mobile sticky button — only during form step */}
          {!isConfirmed && <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50" data-id="element-392">
              <div className="h-6 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none" data-id="element-393" />
              <div className="p-4 bg-[#0a0a0a] border-t border-white/[0.06] pb-8" data-id="element-394">
                <button onClick={handlePay} disabled={isSubmitting} className={`w-full flex justify-center items-center gap-2.5 px-6 py-[18px] rounded-xl font-medium text-[15px] transition-all duration-300 ${isSubmitting ? 'bg-brand-emerald/30 text-white/50 cursor-wait' : 'bg-brand-emerald text-[#0a0a0a]'}`} data-id="element-395">
                  {isSubmitting ? <div className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full animate-spin" data-id="element-396" /> : <>
                      <Lock size={16} data-id="element-397" />
                      <span data-id="element-398">Confirmar reserva · 50,00€</span>
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}